/**
 * Validates that a return URL is safe to use for redirection.
 *
 * This prevents open redirect vulnerabilities by ensuring:
 * - URL is a relative path (starts with /)
 * - URL is not a protocol-relative URL (//evil.com)
 * - URL doesn't contain javascript: or data: protocols
 * - URL doesn't contain embedded credentials (@)
 *
 * @param url - The URL to validate
 * @returns true if the URL is safe to use as a redirect target
 */
export function isValidReturnUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string' || url.length === 0) {
    return false;
  }

  // Must be a relative URL starting with /
  if (!url.startsWith('/')) {
    return false;
  }

  // Block protocol-relative URLs (//evil.com)
  if (url.startsWith('//')) {
    return false;
  }

  // Block javascript: and data: URLs (case-insensitive check in the entire URL)
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.includes('javascript:') || lowerUrl.includes('data:')) {
    return false;
  }

  // Block URLs with embedded credentials (could be used for phishing)
  // e.g., /page@evil.com could be misinterpreted by some browsers
  if (url.includes('@')) {
    return false;
  }

  return true;
}

/**
 * Returns a validated return URL or a fallback if the URL is invalid.
 *
 * @param url - The URL to validate and potentially use
 * @param fallback - The fallback URL to use if the provided URL is invalid (default: '/members')
 * @returns A safe URL to redirect to
 */
export function getValidReturnUrl(url: string | null | undefined, fallback: string = '/members'): string {
  return isValidReturnUrl(url) ? url! : fallback;
}
